/**
 * Central Pricing Constants (cents)
 *
 * Single source of truth for tier-based price values.
 * UI strings, plan IDs, and features live elsewhere (plans-data.ts, LandingPricing.tsx).
 */

export type PriceTier = "go" | "grow" | "glory"

export const MONTHLY_PRICES_CENTS = { go: 19900, grow: 29900, glory: 39900 } as const
export const FIRST_MONTH_PRICES_CENTS = { go: 4900, grow: 6900, glory: 12900 } as const
export const YEARLY_PRICES_CENTS = { go: 34800, grow: 49200, glory: 118800 } as const
export const YEARLY_MONTHLY_EQUIVALENT_CENTS = { go: 2900, grow: 4100, glory: 9900 } as const

/** Yearly plan IDs (used to detect billing period at signup) */
export const YEARLY_PLAN_IDS = new Set(["plan-go-348", "plan-grow-492", "plan-glory-1188"])

/**
 * Full set of yearly plan ids (current + every legacy yearly id that can still be the
 * live `item_price_id` on an un-migrated subscription). Used by the cancel-save flow to
 * BLOCK price-mutating retention offers (downgrade/discount) on yearly subs, which would
 * otherwise apply a MONTHLY downsell cents value to the ANNUAL item price.
 *
 * Source: legacy_to_new_pricing_migration.ts — every legacy id whose `new` is one of
 * plan-go-348 / plan-grow-492 / plan-glory-1188.
 */
export const YEARLY_PLAN_ID_SET = new Set<string>([
    // Current yearly plan ids
    "plan-go-348",
    "plan-grow-492",
    "plan-glory-1188",
    // Legacy yearly ids that map to plan-go-348
    "plan-basic-700",
    "plan-basic-1800",
    "plan-go-1800",
    "plan-350",
    // Legacy yearly ids that map to plan-grow-492
    "plan-pro-1180",
    "plan-pro-2150",
    "plan-grow-2150",
    "plan-699",
    // Legacy yearly ids that map to plan-glory-1188
    "plan-turbo-2140",
    "plan-turbo-3000",
    "plan-glory-3000",
    "plan-1799",
])

/** True when a plan id is any (current or legacy) yearly plan. */
export function isYearlyPlanId(planId: string | null | undefined): boolean {
    return !!planId && YEARLY_PLAN_ID_SET.has(planId)
}

/** Map monthly plan ID → yearly equivalent plan ID (used by switch-to-yearly upsell) */
export const MONTHLY_TO_YEARLY_PLAN: Record<string, string> = {
    "plan-go-199": "plan-go-348",
    "plan-grow-299": "plan-grow-492",
    "plan-glory-399": "plan-glory-1188",
}

/** Map yearly total (cents) → monthly equivalent (cents) for MRR calculations */
export const YEARLY_AMOUNT_TO_MONTHLY: Record<number, number> = {
    [YEARLY_PRICES_CENTS.go]: YEARLY_MONTHLY_EQUIVALENT_CENTS.go,
    [YEARLY_PRICES_CENTS.grow]: YEARLY_MONTHLY_EQUIVALENT_CENTS.grow,
    [YEARLY_PRICES_CENTS.glory]: YEARLY_MONTHLY_EQUIVALENT_CENTS.glory,
}

// ---------------------------------------------------------------------------
// Plan-ID-based pricing maps (used by process_nmi_renewal.ts)
// ---------------------------------------------------------------------------

/**
 * Maps first-month/trial plan IDs to their regular counterparts.
 * If the migration cron (change_first_month_subscriptions) misses a subscription,
 * the safety net in process_nmi_renewal catches it at renewal time.
 */
export const FIRST_MONTH_TO_REGULAR_PLAN: Record<string, string> = {
    // Legacy pricing plans
    "plan-first-month-69": "plan-go-199",
    "plan-1-day-trial-69": "plan-go-199",
    "plan-first-month-99": "plan-grow-299",
    "plan-1-day-trial-99": "plan-grow-299",
    "plan-first-month-199": "plan-glory-399",
    "plan-1-day-trial-199": "plan-glory-399",
    // Legacy pricing with old names
    "plan-basic-149-first": "plan-go-199",
    "plan-basic-69-first": "plan-go-199",
    "plan-pro-199-first": "plan-grow-299",
    "plan-pro-99-first": "plan-grow-299",
    "plan-turbo-299-first": "plan-glory-399",
    "plan-turbo-129-first": "plan-glory-399",
    // Current pricing plans (Go/Grow/Glory)
    "plan-go-49-first": "plan-go-199",
    "plan-go-69-first": "plan-go-199",
    "plan-grow-69-first": "plan-grow-299",
    "plan-grow-99-first": "plan-grow-299",
    "plan-glory-129-first": "plan-glory-399",
}

/**
 * True when the plan id is a FIRST-MONTH promo id (any generation, incl. legacy).
 * Used by the dunning cron to keep never-converted CC-trial rows OFF every
 * repricing path (aggressive Day-3 downsold retry, scheduled downsells, the
 * extended-retry reprice-to-lowest): repricing a first-month sub/invoice desyncs
 * from process_nmi_renewal STEP 2.8's bidirectional first-month clamp, which
 * would re-clamp the charged amount away from the invoice total (display↔charge
 * parity break). First-month rows must only ever retry the existing invoice at
 * its own total.
 */
export function isFirstMonthPlanId(planId: string | null | undefined): boolean {
    return !!planId && planId in FIRST_MONTH_TO_REGULAR_PLAN
}

/**
 * Maps first-month plan IDs to their correct price in cents.
 * Used by CC trial pricing enforcement to override incorrect invoice amounts.
 */
export const FIRST_MONTH_PLAN_PRICES: Record<string, number> = {
    "plan-go-49-first": 4900,
    "plan-go-69-first": 6900,
    "plan-grow-69-first": 6900,
    "plan-grow-99-first": 9900,
    "plan-glory-129-first": 12900,
}

/**
 * Maps regular plan IDs to their first-month price in cents.
 * Used when a CC trial subscription has already been migrated to the regular plan
 * before the first charge — we still enforce first-month pricing.
 */
export const REGULAR_PLAN_TO_FIRST_MONTH: Record<string, number> = {
    "plan-go-199": 4900,
    "plan-grow-299": 6900,
    "plan-glory-399": 12900,
}

/**
 * Maps regular plan IDs to their regular monthly price in cents.
 * Used in first-month plan safety net alerting.
 */
export const REGULAR_PLAN_PRICES: Record<string, number> = {
    "plan-go-199": 19900,
    "plan-grow-299": 29900,
    "plan-glory-399": 39900,
}

/** Legacy tier aliases → current tier names (for create_chargebee_payment.ts compatibility) */
export const LEGACY_TIER_TO_CURRENT: Record<string, PriceTier> = {
    basic: "go", pro: "grow", turbo: "glory",
    go: "go", grow: "grow", glory: "glory",
}
